import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  FiChevronLeft,
  FiChevronRight,
  FiDownload,
  FiMaximize2,
  FiSave,
  FiX,
} from "react-icons/fi";
import type {
  WireTeethModel,
  WireTooth,
} from "../../../utils/smileDesign/dentalAccuracy";
import {
  WIRE_TOOTH_DEFAULT_POINTS,
  WIRE_TOOTH_MAX_POINTS,
  TOOTH_SCALE_MAX,
  TOOTH_SCALE_MIN,
  addWireToothHandle,
  isWireToothEdited,
  removeWireToothHandle,
  resetWireTooth,
  setWireToothTransform,
} from "../../../utils/smileDesign/dentalAccuracy";
import AfterToothCanvas from "./AfterToothCanvas";
import WheelHoverZone from "./WheelHoverZone";
import ZoomableReferenceImage from "./ZoomableReferenceImage";

interface Props {
  open: boolean;
  onClose: () => void;
  afterSrc: string;
  beforeSrc: string | null;
  smileType?: string;
  model: WireTeethModel;
  selectedId: string | null;
  selectedIds?: string[];
  selectedHandle: number | null;
  onSelect: (
    toothId: string | null,
    handleIndex: number | null,
    mode?: "replace" | "toggle" | "focus"
  ) => void;
  onToothChange: (tooth: WireTooth) => void;
  onResetAll: () => void;
  edited: boolean;
  onDownload: () => void;
  onView?: () => void;
  onRefine?: () => void;
  refineBusy?: boolean;
  canRefine?: boolean;
  onSave: () => void;
  actionBusy?: boolean;
  canSave?: boolean;
  saving?: boolean;
}

/**
 * Side-by-side after editor: full after on the left, smile-zoom on the
 * right where tooth width / height change the enamel on the photo.
 */
export default function AfterToothEditView({
  open,
  onClose,
  afterSrc,
  smileType,
  model,
  selectedId,
  selectedIds = [],
  selectedHandle,
  onSelect,
  onToothChange,
  onResetAll,
  edited,
  onDownload,
  onView,
  onRefine,
  refineBusy = false,
  canRefine = false,
  onSave,
  actionBusy = false,
  canSave = false,
  saving = false,
}: Props) {
  const [lockAspect, setLockAspect] = useState(false);
  const teeth = model.teeth;
  const groupIds = selectedIds.length
    ? selectedIds
    : selectedId
      ? [selectedId]
      : [];
  const groupCount = groupIds.length;
  const selectedIndex = teeth.findIndex((t) => t.id === selectedId);
  const selectedTooth = selectedIndex >= 0 ? teeth[selectedIndex] : null;

  const selectToothAt = (index: number) => {
    if (!teeth.length) return;
    const n = teeth.length;
    onSelect(teeth[((index % n) + n) % n].id, null);
  };

  const removeSelectedPoint = () => {
    if (!selectedTooth) return;
    const count = selectedTooth.handles.length;
    if (count <= WIRE_TOOTH_DEFAULT_POINTS) return;
    const index = selectedHandle ?? count - 1;
    onToothChange(removeWireToothHandle(selectedTooth, index));
    onSelect(selectedTooth.id, null);
  };
  const removeRef = useRef(removeSelectedPoint);
  removeRef.current = removeSelectedPoint;
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const typing =
        !!target &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.isContentEditable);
      if (!typing) {
        if (e.key === "Escape" && selectedId) {
          onSelectRef.current(null, null);
          return;
        }
        if ((e.key === "Delete" || e.key === "Backspace") && selectedId) {
          e.preventDefault();
          removeRef.current();
          return;
        }
        if (e.key === "ArrowLeft" && selectedId) {
          e.preventDefault();
          selectToothAt(selectedIndex - 1);
          return;
        }
        if (e.key === "ArrowRight" && selectedId) {
          e.preventDefault();
          selectToothAt(selectedIndex + 1);
          return;
        }
      }
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose, selectedId, selectedIndex]);

  if (!open) return null;

  const widthPct = Math.round((selectedTooth?.scaleX ?? 1) * 100);
  const heightPct = Math.round((selectedTooth?.scaleY ?? 1) * 100);

  return createPortal(
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/70 backdrop-blur-md p-2 sm:p-3"
      role="dialog"
      aria-modal="true"
      aria-labelledby="after-tooth-edit-title"
      onClick={onClose}
    >
      <div
        className="w-full max-w-[min(98vw,1680px)] h-[min(96vh,980px)] flex flex-col rounded-2xl border border-white/10 bg-[#121314] shadow-[0_0_50px_rgba(0,0,0,0.45)] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="shrink-0 flex items-center justify-between gap-3 px-4 sm:px-5 py-3 border-b border-white/10 bg-[#16171a]">
          <div className="min-w-0">
            <div
              id="after-tooth-edit-title"
              className="text-[#2fa9bf] text-xs font-semibold uppercase tracking-wider"
            >
              After · smile zoom
            </div>
            <p className="text-sm text-gray-400 truncate mt-0.5">
              {smileType ? `${smileType} · ` : ""}
              {teeth.length} teeth · resize live on the after smile
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
            {onView && (
              <button
                type="button"
                onClick={() => void onView()}
                disabled={actionBusy}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border border-white/10 text-gray-300 hover:border-[#2fa9bf]/40 disabled:opacity-40"
              >
                <FiMaximize2 className="w-3.5 h-3.5" />
                View
              </button>
            )}
            <button
              type="button"
              onClick={() => void onDownload()}
              disabled={actionBusy}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border border-white/10 text-gray-300 hover:border-[#2fa9bf]/40 disabled:opacity-40"
            >
              <FiDownload className="w-3.5 h-3.5" />
              {actionBusy ? "Building…" : "Download"}
            </button>
            {onRefine && (
              <button
                type="button"
                onClick={() => void onRefine()}
                disabled={actionBusy || refineBusy || !canRefine}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-violet-500/90 text-[#08151A] hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {refineBusy ? "Refining…" : "Refine"}
              </button>
            )}
            <button
              type="button"
              onClick={() => void onSave()}
              disabled={actionBusy || saving || !canSave}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-emerald-500/90 text-[#08151A] hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <FiSave className="w-3.5 h-3.5" />
              {saving || actionBusy ? "Saving…" : canSave ? "Save" : "Saved"}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="w-9 h-9 rounded-lg flex items-center justify-center border border-white/10 text-gray-400 hover:text-white hover:border-white/20"
              aria-label="Close editor"
            >
              <FiX className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-[minmax(220px,0.38fr)_minmax(0,1fr)_240px] xl:grid-cols-[minmax(240px,0.36fr)_minmax(0,1fr)_260px] overflow-hidden">
          <div className="min-h-[200px] lg:min-h-0 h-full p-3 sm:p-4 border-b lg:border-b-0 lg:border-r border-white/10">
            <ZoomableReferenceImage
              src={afterSrc}
              label="After"
              emptyText="No after image"
            />
          </div>

          <div className="min-h-0 p-3 sm:p-4 flex flex-col">
            <AfterToothCanvas
              afterSrc={afterSrc}
              model={model}
              selectedId={selectedId}
              selectedIds={groupIds}
              selectedHandle={selectedHandle}
              lockAspect={lockAspect}
              onSelect={onSelect}
              onToothChange={onToothChange}
            />
          </div>

          <aside className="border-t lg:border-t-0 lg:border-l border-white/10 bg-[#141516] p-3 sm:p-4 flex flex-col gap-4 overflow-y-auto">
            <div className="space-y-2">
              <p className="text-xs uppercase tracking-wider text-[#2fa9bf] font-semibold">
                Selected tooth
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => selectToothAt(selectedIndex - 1)}
                  className="w-7 h-7 rounded-md flex items-center justify-center border border-white/10 text-gray-400 hover:border-white/20"
                  aria-label="Previous tooth"
                >
                  <FiChevronLeft className="w-4 h-4" />
                </button>
                <div className="flex-1 text-center text-xs">
                  {selectedTooth ? (
                    <>
                      <span className="text-white font-semibold">
                        {groupCount > 1
                          ? `${groupCount} teeth`
                          : `Tooth ${selectedIndex + 1}`}
                      </span>
                      <span className="text-gray-500"> / {teeth.length}</span>
                      <span className="block text-[#2fa9bf] font-mono">
                        {groupCount > 1
                          ? "width & height linked"
                          : `${selectedTooth.handles.length} / ${WIRE_TOOTH_MAX_POINTS} points`}
                      </span>
                    </>
                  ) : (
                    <span className="text-gray-400">Click a tooth</span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => selectToothAt(selectedIndex + 1)}
                  className="w-7 h-7 rounded-md flex items-center justify-center border border-white/10 text-gray-400 hover:border-white/20"
                  aria-label="Next tooth"
                >
                  <FiChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>

            {selectedTooth && (
              <>
                <div className="space-y-3">
                  <p className="text-xs uppercase tracking-wider text-[#2fa9bf] font-semibold">
                    Size{groupCount > 1 ? ` · ${groupCount} teeth` : ""}
                  </p>
                  {groupCount > 1 && (
                    <p className="text-[11px] text-[#8b8f8d] leading-snug">
                      Width and height changes apply to every selected tooth.
                      Ctrl+click a tooth to add or remove it.
                    </p>
                  )}
                  <button
                    type="button"
                    role="switch"
                    aria-checked={lockAspect}
                    onClick={() => setLockAspect((v) => !v)}
                    className={`px-2.5 py-1.5 rounded-md text-xs font-semibold border transition-colors ${
                      lockAspect
                        ? "bg-[#2fa9bf]/20 border-[#2fa9bf]/50 text-[#2fa9bf]"
                        : "border-white/10 text-gray-400 hover:border-white/20"
                    }`}
                  >
                    {lockAspect ? "Ratio locked" : "Lock ratio"}
                  </button>
                  <WheelHoverZone
                    className="block space-y-1.5"
                    min={Math.round(TOOTH_SCALE_MIN * 100)}
                    max={Math.round(TOOTH_SCALE_MAX * 100)}
                    step={1}
                    value={widthPct}
                    onValueChange={(next) => {
                      const scaleX = next / 100;
                      onToothChange(
                        setWireToothTransform(selectedTooth, {
                          scaleX,
                          scaleY: lockAspect ? scaleX : selectedTooth.scaleY,
                        })
                      );
                    }}
                  >
                    <label className="block space-y-1.5">
                      <span className="text-xs text-[#8b8f8d] flex justify-between gap-2">
                        <span>Width</span>
                        <span className="text-[#2fa9bf] font-mono">{widthPct}%</span>
                      </span>
                      <input
                        type="range"
                        min={Math.round(TOOTH_SCALE_MIN * 100)}
                        max={Math.round(TOOTH_SCALE_MAX * 100)}
                        step={1}
                        value={widthPct}
                        onChange={(e) => {
                          const scaleX = Number(e.target.value) / 100;
                          onToothChange(
                            setWireToothTransform(selectedTooth, {
                              scaleX,
                              scaleY: lockAspect ? scaleX : selectedTooth.scaleY,
                            })
                          );
                        }}
                        className="w-full accent-[#2fa9bf]"
                      />
                    </label>
                  </WheelHoverZone>
                  <WheelHoverZone
                    className="block space-y-1.5"
                    min={Math.round(TOOTH_SCALE_MIN * 100)}
                    max={Math.round(TOOTH_SCALE_MAX * 100)}
                    step={1}
                    value={heightPct}
                    onValueChange={(next) => {
                      const scaleY = next / 100;
                      onToothChange(
                        setWireToothTransform(selectedTooth, {
                          scaleY,
                          scaleX: lockAspect ? scaleY : selectedTooth.scaleX,
                        })
                      );
                    }}
                  >
                    <label className="block space-y-1.5">
                      <span className="text-xs text-[#8b8f8d] flex justify-between gap-2">
                        <span>Height</span>
                        <span className="text-[#2fa9bf] font-mono">{heightPct}%</span>
                      </span>
                      <input
                        type="range"
                        min={Math.round(TOOTH_SCALE_MIN * 100)}
                        max={Math.round(TOOTH_SCALE_MAX * 100)}
                        step={1}
                        value={heightPct}
                        onChange={(e) => {
                          const scaleY = Number(e.target.value) / 100;
                          onToothChange(
                            setWireToothTransform(selectedTooth, {
                              scaleY,
                              scaleX: lockAspect ? scaleY : selectedTooth.scaleX,
                            })
                          );
                        }}
                        className="w-full accent-[#2fa9bf]"
                      />
                    </label>
                  </WheelHoverZone>
                </div>

                <div className="space-y-2">
                  <p className="text-xs uppercase tracking-wider text-[#2fa9bf] font-semibold">
                    Shape
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      disabled={selectedTooth.handles.length >= WIRE_TOOTH_MAX_POINTS}
                      onClick={() =>
                        onToothChange(addWireToothHandle(selectedTooth))
                      }
                      className="px-2.5 py-1.5 rounded-md text-xs font-semibold border border-[#2fa9bf]/40 text-[#2fa9bf] hover:bg-[#2fa9bf]/15 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      Add point
                    </button>
                    <button
                      type="button"
                      disabled={selectedTooth.handles.length <= WIRE_TOOTH_DEFAULT_POINTS}
                      onClick={removeSelectedPoint}
                      className="px-2.5 py-1.5 rounded-md text-xs font-semibold border border-white/10 text-gray-300 hover:border-white/20 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      Remove point
                    </button>
                    <button
                      type="button"
                      disabled={!isWireToothEdited(selectedTooth)}
                      onClick={() => {
                        onToothChange(resetWireTooth(selectedTooth));
                        onSelect(selectedTooth.id, null);
                      }}
                      className="px-2.5 py-1.5 rounded-md text-xs font-semibold border border-white/10 text-gray-300 hover:border-white/20 disabled:opacity-40"
                    >
                      Reset tooth
                    </button>
                  </div>
                  <p className="text-[11px] text-[#8b8f8d] leading-snug">
                    Width and height change the tooth on the after photo.
                    Ctrl+click selects more than one; the same width or height
                    change then applies to all of them. Yellow squares resize.
                    White dots reshape only the active tooth.
                  </p>
                </div>
              </>
            )}

            {edited && (
              <button
                type="button"
                onClick={onResetAll}
                className="px-2.5 py-1.5 rounded-md text-xs font-semibold border border-white/10 text-gray-400 hover:border-white/20"
              >
                Reset all teeth
              </button>
            )}
          </aside>
        </div>
      </div>
    </div>,
    document.body
  );
}
